import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { studioHeaders } from "@/lib/studioAuth";
import { Loader2, CheckCircle2, Clock, MailCheck, Hourglass, AlertCircle, RotateCw, Copy, ExternalLink, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge colour map — mirrors the backend enum
const STATUS_META = {
    pending_deposit: { label: "Pending deposit", color: "bg-cafe-mocha/15 text-cafe-mocha", Icon: Hourglass },
    deposit_paid: { label: "Deposit paid · awaiting shoot", color: "bg-cafe-olive/15 text-cafe-olive", Icon: Clock },
    delivered: { label: "Delivered · balance email in 24h", color: "bg-[#D4A574]/15 text-[#B88A55]", Icon: MailCheck },
    balance_email_sent: { label: "Awaiting balance payment", color: "bg-cafe-terracotta/15 text-cafe-terracotta", Icon: Hourglass },
    paid_full: { label: "Paid full ✓", color: "bg-cafe-olive text-cafe-cream", Icon: CheckCircle2 },
    cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700", Icon: X },
};

function fmtRM(cents) {
    if (cents == null) return "—";
    return `RM ${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return iso;
    }
}

function StatusBadge({ status }) {
    const m = STATUS_META[status] || { label: status, color: "bg-cafe-mocha/15 text-cafe-mocha", Icon: AlertCircle };
    const Icon = m.Icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${m.color}`}>
            <Icon className="h-3 w-3" strokeWidth={2} />
            {m.label}
        </span>
    );
}

function DeliverModal({ order, open, onClose, onDone }) {
    const [previewUrl, setPreviewUrl] = useState("");
    const [filesUrl, setFilesUrl] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open && order) {
            setPreviewUrl(order.delivery_preview_url || "");
            setFilesUrl(order.delivery_files_url || "");
            setNotes(order.delivery_notes || "");
        }
    }, [open, order]);

    if (!order) return null;

    const submit = async () => {
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/admin/shoot-orders/${order.id}/mark-delivered`,
                { delivery_preview_url: previewUrl || null, delivery_files_url: filesUrl || null, delivery_notes: notes || null },
                { headers: studioHeaders() },
            );
            toast.success("Marked delivered · balance email in 24h");
            onDone?.();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to mark delivered");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-cafe-cream border border-cafe-espresso/10 rounded-2xl max-w-lg" data-testid="deliver-modal">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-cafe-espresso">Mark as delivered</DialogTitle>
                    <DialogDescription className="text-cafe-mocha text-sm">
                        Customer will receive the balance-payment email <strong>24 hours</strong> later.
                        Click <em>Send balance email now</em> from the order row to skip the delay.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div>
                        <Label className="text-xs text-cafe-mocha font-semibold uppercase tracking-[0.15em]">Preview link (watermarked) · optional</Label>
                        <Input
                            value={previewUrl}
                            onChange={(e) => setPreviewUrl(e.target.value)}
                            placeholder="https://drive.google.com/preview..."
                            data-testid="deliver-preview-url"
                            className="mt-1.5 bg-white border-cafe-espresso/10 rounded-xl text-sm"
                        />
                        <p className="text-[11px] text-cafe-mocha/70 mt-1">Shown in the balance email so customer can preview before paying.</p>
                    </div>
                    <div>
                        <Label className="text-xs text-cafe-mocha font-semibold uppercase tracking-[0.15em]">Full files link (unwatermarked) · optional</Label>
                        <Input
                            value={filesUrl}
                            onChange={(e) => setFilesUrl(e.target.value)}
                            placeholder="https://drive.google.com/folder..."
                            data-testid="deliver-files-url"
                            className="mt-1.5 bg-white border-cafe-espresso/10 rounded-xl text-sm"
                        />
                        <p className="text-[11px] text-cafe-mocha/70 mt-1">Sent in the final files-unlocked email after balance is paid. Can also be set later.</p>
                    </div>
                    <div>
                        <Label className="text-xs text-cafe-mocha font-semibold uppercase tracking-[0.15em]">Notes for the customer · optional</Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Hand-edited 40 shots — leaned warmer on your latte hero."
                            data-testid="deliver-notes"
                            rows={3}
                            className="mt-1.5 bg-white border-cafe-espresso/10 rounded-xl text-sm"
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button variant="outline" onClick={onClose} className="rounded-full bg-transparent">Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={submitting}
                        data-testid="deliver-submit"
                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream"
                    >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Mark delivered
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function OrderRow({ order, onRefresh }) {
    const [showDeliver, setShowDeliver] = useState(false);
    const [actionLoading, setActionLoading] = useState(null); // string action key
    const [balanceUrl, setBalanceUrl] = useState(null);

    const fetchBalanceUrl = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/shoot-orders/${order.id}`, { headers: studioHeaders() });
            if (data?.balance_url) setBalanceUrl(data.balance_url);
            return data?.balance_url;
        } catch {
            return null;
        }
    };

    const copyBalanceLink = async () => {
        const url = balanceUrl || (await fetchBalanceUrl());
        if (!url) return toast.error("Could not load balance link");
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Balance link copied · paste into WhatsApp");
        } catch {
            toast.error("Copy failed — open dev tools to grab it");
        }
    };

    const sendBalanceNow = async () => {
        if (!window.confirm(`Skip the 24h delay for ${order.customer_name || order.customer_email || "this client"} and send the balance email immediately?`)) return;
        setActionLoading("balance-now");
        try {
            await axios.post(`${API}/admin/shoot-orders/${order.id}/send-balance-now`, {}, { headers: studioHeaders() });
            toast.success("Balance email sent");
            onRefresh?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setActionLoading(null);
        }
    };

    const resendReminder = async () => {
        setActionLoading("reminder");
        try {
            await axios.post(`${API}/admin/shoot-orders/${order.id}/resend-balance-reminder`, {}, { headers: studioHeaders() });
            toast.success("Reminder sent");
            onRefresh?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setActionLoading(null);
        }
    };

    const cancelOrder = async () => {
        if (!window.confirm(`Cancel the project for ${order.customer_name || order.customer_email || "this client"}? This does NOT refund. Process refund manually in Stripe Dashboard first.`)) return;
        setActionLoading("cancel");
        try {
            await axios.post(`${API}/admin/shoot-orders/${order.id}/cancel`, {}, { headers: studioHeaders() });
            toast.success("Order cancelled");
            onRefresh?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div
            data-testid={`shoot-order-${order.id}`}
            className="bg-cafe-cream border border-cafe-espresso/10 rounded-2xl p-5 md:p-6 mb-3"
        >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <div className="font-display text-lg text-cafe-espresso leading-tight">{order.package_name_en}</div>
                    <div className="text-sm text-cafe-mocha mt-0.5">
                        <a href={`mailto:${order.customer_email}`} className="hover:text-cafe-olive">{order.customer_email}</a>
                        {order.customer_name ? ` · ${order.customer_name}` : ""}
                        {order.customer_phone ? (
                            <> · <a href={`https://wa.me/${(order.customer_phone || "").replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="hover:text-cafe-olive">{order.customer_phone}</a></>
                        ) : null}
                    </div>
                </div>
                <StatusBadge status={order.status} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                <div>
                    <div className="text-cafe-mocha/70 uppercase tracking-[0.15em] mb-0.5 text-[10px]">Total</div>
                    <div className="font-mono text-cafe-espresso">{fmtRM(order.total_cents)}</div>
                </div>
                <div>
                    <div className="text-cafe-mocha/70 uppercase tracking-[0.15em] mb-0.5 text-[10px]">Deposit</div>
                    <div className="font-mono text-cafe-olive">{fmtRM(order.deposit_cents)} {order.deposit_paid_at && "✓"}</div>
                </div>
                <div>
                    <div className="text-cafe-mocha/70 uppercase tracking-[0.15em] mb-0.5 text-[10px]">Balance</div>
                    <div className={`font-mono ${order.balance_paid_at ? "text-cafe-olive" : "text-cafe-mocha"}`}>
                        {fmtRM(order.balance_cents)} {order.balance_paid_at && "✓"}
                    </div>
                </div>
                <div>
                    <div className="text-cafe-mocha/70 uppercase tracking-[0.15em] mb-0.5 text-[10px]">Booked</div>
                    <div className="font-mono text-cafe-mocha text-[11px]">{fmtDate(order.created_at)}</div>
                </div>
            </div>

            {(order.delivery_preview_url || order.delivery_files_url || order.delivery_notes) && (
                <div className="bg-cafe-latte/40 rounded-xl p-3 mb-4 text-[12px] space-y-1.5">
                    {order.delivery_preview_url && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-cafe-mocha/70">Preview:</span>
                            <a href={order.delivery_preview_url} target="_blank" rel="noopener noreferrer" className="text-cafe-olive truncate hover:underline">{order.delivery_preview_url}</a>
                        </div>
                    )}
                    {order.delivery_files_url && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-cafe-mocha/70">Files:</span>
                            <a href={order.delivery_files_url} target="_blank" rel="noopener noreferrer" className="text-cafe-olive truncate hover:underline">{order.delivery_files_url}</a>
                        </div>
                    )}
                    {order.delivery_notes && <p className="text-cafe-mocha italic">"{order.delivery_notes}"</p>}
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                {/* Mark Delivered — for deposit_paid orders */}
                {order.status === "deposit_paid" && (
                    <Button
                        onClick={() => setShowDeliver(true)}
                        data-testid={`deliver-btn-${order.id}`}
                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream text-xs h-9 px-4"
                    >
                        Mark delivered
                    </Button>
                )}

                {/* Send Balance Email Now — for delivered orders */}
                {order.status === "delivered" && (
                    <Button
                        onClick={sendBalanceNow}
                        disabled={actionLoading === "balance-now"}
                        data-testid={`send-balance-${order.id}`}
                        className="rounded-full bg-[#B88A55] hover:bg-[#9d743f] text-cafe-cream text-xs h-9 px-4"
                    >
                        {actionLoading === "balance-now" ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <MailCheck className="h-3 w-3 mr-1.5" />}
                        Send balance email now (skip 24h)
                    </Button>
                )}

                {/* Resend Reminder — for balance_email_sent orders */}
                {order.status === "balance_email_sent" && (
                    <Button
                        onClick={resendReminder}
                        disabled={actionLoading === "reminder"}
                        data-testid={`resend-${order.id}`}
                        variant="outline"
                        className="rounded-full bg-transparent border-cafe-espresso/20 text-cafe-espresso text-xs h-9 px-4"
                    >
                        {actionLoading === "reminder" ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <RotateCw className="h-3 w-3 mr-1.5" />}
                        Resend reminder
                    </Button>
                )}

                {/* Copy balance link — for delivered / balance_email_sent — useful for WhatsApp */}
                {(order.status === "delivered" || order.status === "balance_email_sent") && (
                    <Button
                        onClick={copyBalanceLink}
                        data-testid={`copy-link-${order.id}`}
                        variant="outline"
                        className="rounded-full bg-transparent border-cafe-espresso/20 text-cafe-espresso text-xs h-9 px-4"
                    >
                        <Copy className="h-3 w-3 mr-1.5" /> Copy balance link
                    </Button>
                )}

                {/* Edit delivery details — for delivered / balance_email_sent — change file links etc */}
                {(order.status === "delivered" || order.status === "balance_email_sent" || order.status === "paid_full") && (
                    <Button
                        onClick={() => setShowDeliver(true)}
                        variant="outline"
                        className="rounded-full bg-transparent border-cafe-espresso/20 text-cafe-espresso text-xs h-9 px-4"
                    >
                        Edit delivery
                    </Button>
                )}

                {/* Stripe dashboard link */}
                {order.deposit_payment_intent && (
                    <a
                        href={`https://dashboard.stripe.com/payments/${order.deposit_payment_intent}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full border border-cafe-espresso/20 px-4 h-9 text-xs text-cafe-mocha hover:text-cafe-espresso transition-colors"
                    >
                        <ExternalLink className="h-3 w-3" /> Stripe deposit
                    </a>
                )}

                {/* Cancel — destructive, hidden behind confirm */}
                {order.status !== "cancelled" && order.status !== "paid_full" && (
                    <button
                        onClick={cancelOrder}
                        disabled={actionLoading === "cancel"}
                        data-testid={`cancel-${order.id}`}
                        className="ml-auto text-[11px] text-cafe-mocha/50 hover:text-red-600 underline underline-offset-2"
                    >
                        Cancel order
                    </button>
                )}
            </div>

            {order.balance_email_reminders && order.balance_email_reminders.length > 0 && (
                <div className="mt-3 pt-3 border-t border-cafe-espresso/10 text-[11px] text-cafe-mocha/70">
                    Reminders sent: {order.balance_email_reminders.length} ({order.balance_email_reminders.map((r) => `D${r.day_offset || "?"}`).join(", ")})
                </div>
            )}

            <DeliverModal
                order={order}
                open={showDeliver}
                onClose={() => setShowDeliver(false)}
                onDone={onRefresh}
            />
        </div>
    );
}


export default function ShootOrdersTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    const load = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const params = filter ? { status: filter } : {};
            const { data } = await axios.get(`${API}/admin/shoot-orders`, { params, headers: studioHeaders() });
            setOrders(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load orders");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

    const runCron = async () => {
        try {
            const { data } = await axios.post(`${API}/admin/shoot-orders/cron-tick`, {}, { headers: studioHeaders() });
            toast.success(`Cron: ${data.sent_balance_emails} balance · ${data.sent_reminder_emails} reminders`);
            load(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Cron failed");
        }
    };

    const counts = orders.reduce((acc, o) => {
        acc[o.status] = (acc[o.status] || 0) + 1;
        return acc;
    }, {});

    const filters = [
        { key: "", label: "All", count: orders.length },
        { key: "deposit_paid", label: "Awaiting shoot", count: counts.deposit_paid || 0 },
        { key: "delivered", label: "Delivered", count: counts.delivered || 0 },
        { key: "balance_email_sent", label: "Awaiting balance", count: counts.balance_email_sent || 0 },
        { key: "paid_full", label: "Paid full", count: counts.paid_full || 0 },
    ];

    return (
        <div data-testid="orders-tab">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h2 className="font-display text-2xl text-cafe-espresso leading-tight">Shoot orders</h2>
                    <p className="text-cafe-mocha text-sm">50% deposit + 50% balance after delivery · auto emails + Stripe reconciled</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => load()}
                        variant="outline"
                        data-testid="orders-refresh"
                        className="rounded-full bg-transparent border-cafe-espresso/20 text-cafe-espresso h-9 px-4 text-xs"
                    >
                        <RotateCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
                    </Button>
                </div>
            </div>

            {/* Status filter chips */}
            <div className="flex flex-wrap gap-2 mb-6">
                {filters.map((f) => (
                    <button
                        key={f.key || "all"}
                        onClick={() => setFilter(f.key)}
                        data-testid={`orders-filter-${f.key || "all"}`}
                        className={`text-xs px-3.5 py-1.5 rounded-full transition-colors ${filter === f.key
                            ? "bg-cafe-espresso text-cafe-cream"
                            : "bg-transparent border border-cafe-espresso/15 text-cafe-mocha hover:bg-cafe-latte"
                            }`}
                    >
                        {f.label} <span className="ml-1 opacity-60">{f.count}</span>
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-cafe-mocha py-12 text-center">
                    <Loader2 className="h-6 w-6 mx-auto animate-spin mb-3" />
                    Loading orders…
                </div>
            ) : orders.length === 0 ? (
                <div className="text-cafe-mocha/70 py-16 text-center bg-cafe-cream/50 border border-dashed border-cafe-espresso/15 rounded-2xl">
                    <p className="text-sm italic">No orders yet.</p>
                    <p className="text-xs mt-2">Orders will appear here once a customer completes their deposit checkout.</p>
                </div>
            ) : (
                <div>
                    {orders.map((o) => (
                        <OrderRow key={o.id} order={o} onRefresh={() => load(true)} />
                    ))}
                </div>
            )}
        </div>
    );
}
